import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon, Loading } from "@green-on-grey/ui-kit";
import { AgentButton, Chat, ChatLayout, ConversationList } from "@green-on-grey/ui-kit/chat";
import { ApiError } from "../api/client";
import { useAgent } from "../api/queries";
import type { Conversation } from "../api/types";
import { unseenImages, useAgentPatch, useConversation, useConversations } from "./adapter";
import { AgentMenu } from "./AgentMenu";
import { CommitmentsPanel } from "./CommitmentsPanel";
import { TONE_ICONS } from "./tones";

/** The chat with the personal agent (R30, design §3.2): the main
 * conversation and topics on the left; the conversation on the right with
 * channel marks, folded tool steps, model errors with Retry, attachments and
 * voice input checked before sending. The list and the conversation are the
 * components of the kit (FTR.NAB.CMN-0005 R11); the data comes from ./adapter. */
export function ChatPage() {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [sideOpen, setSideOpen] = useState(false);
  const { all, rows, main, create } = useConversations();
  const current = routeId ? all.find((c) => c.id === routeId) : main;
  const open = (c: Conversation) => navigate(c.kind === "main" ? "/" : `/chat/${c.id}`);

  // The sidebar closes when another conversation opens.
  const [prevRouteId, setPrevRouteId] = useState(routeId);
  if (prevRouteId !== routeId) {
    setPrevRouteId(routeId);
    setSideOpen(false);
  }

  return (
    <ChatLayout>
      <ConversationList conversations={rows} currentId={current?.id} open={sideOpen} onClose={() => setSideOpen(false)}
        onPick={(id) => { setSideOpen(false); const c = all.find((x) => x.id === id); if (c) open(c); }}
        onCreate={async (title) => navigate(`/chat/${(await create(title)).id}`)} />
      {current ? (
        <ConversationView key={current.id} conv={current} onMenu={() => setSideOpen((v) => !v)} />
      ) : (
        <div className="nb-chat"><Loading /></div>
      )}
      <CommitmentsPanel />
    </ChatLayout>
  );
}

function ConversationView({ conv, onMenu }: { conv: Conversation; onMenu: () => void }) {
  const { t } = useTranslation();
  const { data: agent } = useAgent();
  const patchAgent = useAgentPatch();
  const [agentMenu, setAgentMenu] = useState(false);
  const closeAgentMenu = useCallback(() => setAgentMenu(false), []);
  const { unarchive, pendingImages, ...chat } = useConversation(conv, agent?.model?.caps.contextWindow);
  // FTR.NAB.CHAT-0001 R11: the model does not see images — say so and offer one that does
  const imagesUnseen = unseenImages(agent, pendingImages);
  const seeingModel = imagesUnseen?.seeing;
  return (
    <Chat {...chat} agentName={agent?.name ?? "Nabu"} emptyHint={t("chat.emptyHint")}
      aboveComposer={imagesUnseen ? (
        <div className="img-warn" role="status">
          <Icon name="image" size={14} />
          <span>{t("chat.imageNotSeen", { model: agent?.model?.name })}</span>
          {seeingModel && (
            <button className="btn ghost sm push" disabled={patchAgent.isPending}
              onClick={() => patchAgent.mutate({ model: { connectionId: seeingModel.connectionId, model: seeingModel.model } })}>
              {t("chat.switchTo", { model: seeingModel.name })}
            </button>
          )}
        </div>
      ) : undefined}
      readOnly={conv.archivedAt !== null && (
        <>
          <span className="small muted">{t("chat.archivedHint")}</span>
          <button className="btn sm" onClick={unarchive}>{t("chat.unarchive")}</button>
        </>
      )}
      header={(
        <>
          <div className="hd">
            <button className="iconbtn show-m" onClick={onMenu} aria-label={t("chat.conversations")}><Icon name="msg2" /></button>
            {agent && (
              <AgentButton icon={TONE_ICONS[agent.tone] ?? "tBusiness"} label={t("agent.menu")} title={t(`tones.${agent.tone}`)}
                expanded={agentMenu} onClick={() => setAgentMenu((v) => !v)} />
            )}
            <div style={{ minWidth: 0 }}>
              <b>{agent?.name ?? "…"}</b>
              <div className="small muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="cpu" size={12} />
                {agent?.model ? <span className="mono" title={agent.model.connection}>{agent.model.name}</span> : <span>{t("chat.noModel")}</span>}
              </div>
            </div>
            <span className="spacer" />
            <span className="small muted hide-m">{conv.kind === "main" ? t("chat.mainHint") : <>{conv.source === "email" && <Icon name="mail" size={13} />} {conv.title}</>}</span>
          </div>
          {agentMenu && agent && <AgentMenu agent={agent} onClose={closeAgentMenu} />}
        </>
      )} />
  );
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
